import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Bell, Clock, Mail, Calendar, CheckCircle, AlertCircle,
  Send, Settings, Plus, Trash2, Edit, ToggleLeft, ToggleRight,
  FileCheck, Stethoscope, User, RefreshCw, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AutomatedReminders = () => {
  const [reminders, setReminders] = useState([]);
  const [pendingReminders, setPendingReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewReminder, setShowNewReminder] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [activityLog, setActivityLog] = useState([]);

  // Fetch reminders from backend
  const fetchReminders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/reminders`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReminders(data.reminders.map(r => ({
          id: r.id,
          name: r.name,
          type: r.type,
          trigger: r.trigger,
          enabled: r.enabled,
          template: r.template,
          sentCount: r.sent_count || 0,
          lastRun: r.last_run || 'Never',
          nextRun: r.enabled ? 'Scheduled' : 'Disabled'
        })));
      }
    } catch (err) {
      console.error('Failed to fetch reminders:', err);
    }
  }, []);

  // Fetch pending reminders
  const fetchPendingReminders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/reminders/pending`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPendingReminders(data.pending.map(p => ({
          id: p.id,
          subject: p.subject_name || p.subject_id,
          type: p.type,
          message: p.message,
          scheduled: p.scheduled
        })));
      }
    } catch (err) {
      console.error('Failed to fetch pending reminders:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReminders();
    fetchPendingReminders();
  }, [fetchReminders, fetchPendingReminders]);

  const toggleReminder = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/registry/reminders/${id}/toggle`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReminders(reminders.map(r => 
          r.id === id ? { ...r, enabled: data.enabled, nextRun: data.enabled ? 'Scheduled' : 'Disabled' } : r
        ));
        toast.success('Reminder setting updated');
      }
    } catch (err) {
      toast.error('Failed to update reminder');
    }
  };

  const sendManualReminder = (pending) => {
    // Add to activity log
    setActivityLog(prev => [{
      id: Date.now(),
      type: pending.type,
      subject: pending.subject,
      status: 'sent',
      timestamp: new Date().toLocaleString()
    }, ...prev.slice(0, 9)]);
    
    toast.success(`Reminder sent to ${pending.subject}`);
    setPendingReminders(pendingReminders.filter(p => p.id !== pending.id));
  };

  const deleteReminder = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/registry/reminders/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setReminders(reminders.filter(r => r.id !== id));
        toast.success('Reminder deleted');
      }
    } catch (err) {
      toast.error('Failed to delete reminder');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'consent':
        return <FileCheck className="w-4 h-4 text-blue-600" />;
      case 'visit':
        return <Stethoscope className="w-4 h-4 text-green-600" />;
      case 'assessment':
        return <AlertCircle className="w-4 h-4 text-orange-600" />;
      default:
        return <Bell className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'consent':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Consent</span>;
      case 'visit':
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Visit</span>;
      case 'assessment':
        return <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">Assessment</span>;
      default:
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">Other</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{reminders.filter(r => r.enabled).length}</p>
                <p className="text-xs text-blue-600">Active Rules</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">{pendingReminders.length}</p>
                <p className="text-xs text-orange-600">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Send className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">
                  {reminders.reduce((acc, r) => acc + r.sentCount, 0)}
                </p>
                <p className="text-xs text-green-600">Sent Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">98%</p>
                <p className="text-xs text-purple-600">Delivery Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Reminder Rules */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium text-gray-700 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-[#1877F2]" />
                Reminder Rules
              </CardTitle>
              <Button size="sm" className="bg-[#1877F2] hover:bg-[#166FE5]">
                <Plus className="w-4 h-4 mr-1" />
                Add Rule
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reminders.map((reminder) => (
                <div 
                  key={reminder.id}
                  className={`p-3 rounded-lg border transition-all ${
                    reminder.enabled ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(reminder.type)}
                      <span className="font-medium text-gray-900 text-sm">{reminder.name}</span>
                    </div>
                    <button
                      onClick={() => toggleReminder(reminder.id)}
                      className="focus:outline-none"
                    >
                      {reminder.enabled ? (
                        <ToggleRight className="w-6 h-6 text-green-500" />
                      ) : (
                        <ToggleLeft className="w-6 h-6 text-gray-400" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-2 line-clamp-1">{reminder.template}</p>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      {getTypeBadge(reminder.type)}
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-500">{reminder.sentCount} sent</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        <Edit className="w-3 h-3 text-gray-400" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                        <Trash2 className="w-3 h-3 text-gray-400" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* SMTP Notice */}
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-amber-800">Email Configuration Required</p>
                  <p className="text-xs text-amber-700">Configure SMTP credentials in Settings to enable automated emails.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Reminders Queue */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium text-gray-700 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-orange-600" />
                Pending Reminders
              </CardTitle>
              <Button variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingReminders.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No pending reminders</p>
                </div>
              ) : (
                pendingReminders.map((pending) => (
                  <div 
                    key={pending.id}
                    className="p-3 rounded-lg border border-gray-200 bg-white hover:border-gray-300 transition-all"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center font-bold text-gray-600 text-xs">
                          {pending.subject.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{pending.subject}</p>
                          <p className="text-xs text-gray-500">{pending.message}</p>
                        </div>
                      </div>
                      {getTypeBadge(pending.type)}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {pending.scheduled}
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => sendManualReminder(pending)}
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Send Now
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium text-gray-700 flex items-center">
            <Mail className="w-5 h-5 mr-2 text-green-600" />
            Recent Reminder Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[
              { subject: 'J. Smith', type: 'consent', status: 'delivered', time: '10 min ago' },
              { subject: 'M. Johnson', type: 'visit', status: 'delivered', time: '1 hour ago' },
              { subject: 'R. Williams', type: 'visit', status: 'opened', time: '2 hours ago' },
              { subject: 'A. Brown', type: 'consent', status: 'clicked', time: '3 hours ago' },
              { subject: 'T. Davis', type: 'assessment', status: 'bounced', time: '5 hours ago' },
            ].map((activity) => (
              <div key={`activity-${activity.subject}-${activity.type}-${activity.time}`} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                <div className="flex items-center gap-3">
                  {getTypeIcon(activity.type)}
                  <span className="text-sm text-gray-700">{activity.subject}</span>
                  {getTypeBadge(activity.type)}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    activity.status === 'delivered' ? 'bg-green-100 text-green-700' :
                    activity.status === 'opened' ? 'bg-blue-100 text-blue-700' :
                    activity.status === 'clicked' ? 'bg-purple-100 text-purple-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {activity.status}
                  </span>
                  <span className="text-xs text-gray-400">{activity.time}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AutomatedReminders;
